import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, MessageCircle, ChevronDown } from "lucide-react";
import { EASE } from "./motion";
import { scrollToSection, waLink, SPECIALIST_MESSAGE } from "../lib/config";

const LINES = [
  { text: "Sua clínica não precisa" },
  { text: "apenas de marketing." },
  { text: "Precisa de um" },
  { text: "sistema de crescimento.", gold: true },
];

const NODES = [
  { label: "Marketing", a: -90 },
  { label: "Comercial", a: 0 },
  { label: "Hub", a: 90 },
  { label: "Academy", a: 180 },
];

function Ecosystem() {
  return (
    <div className="relative w-full aspect-square" data-testid="hero-ecosystem">
      <svg viewBox="0 0 520 520" className="w-full h-full">
        <circle cx="260" cy="260" r="90" fill="none" stroke="#E9B83F" strokeOpacity="0.18" />
        <circle cx="260" cy="260" r="150" fill="none" stroke="#E9B83F" strokeOpacity="0.12" strokeDasharray="3 8" />
        <circle cx="260" cy="260" r="200" fill="none" stroke="#E9B83F" strokeOpacity="0.2" />
        <circle cx="260" cy="260" r="4" fill="#E9B83F" fillOpacity="0.9" />
        <g className="animate-orbit">
          <circle cx="260" cy="60" r="5" fill="#E9B83F" />
          <circle cx="460" cy="260" r="4" fill="#E9B83F" fillOpacity="0.7" />
        </g>
        <g className="animate-orbit-reverse">
          <circle cx="260" cy="110" r="3.5" fill="#F5F2EB" fillOpacity="0.7" />
          <circle cx="110" cy="260" r="3.5" fill="#E9B83F" fillOpacity="0.6" />
        </g>
      </svg>

      {NODES.map((n, i) => {
        const x = 50 + (200 / 520) * 100 * Math.cos((n.a * Math.PI) / 180) * 1.15;
        const y = 50 + (200 / 520) * 100 * Math.sin((n.a * Math.PI) / 180) * 1.15;
        return (
          <div
            key={n.label}
            className="absolute animate-float-y hidden lg:block"
            style={{ left: `${x}%`, top: `${y}%`, transform: "translate(-50%,-50%)", animationDelay: `${i * 0.9}s` }}
          >
            <span className="inline-flex items-center gap-2 bg-graphite/80 backdrop-blur border border-gold/25 rounded-full font-semibold tracking-widest uppercase text-bone/90 px-4 py-1.5 text-xs">
              <span className="w-1.5 h-1.5 rounded-full bg-gold" />
              {n.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const ecoY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative min-h-screen bg-ink overflow-hidden flex items-center">
      <div className="absolute inset-0 bg-grid-gold" />
      <motion.div
        style={reduce ? {} : { y: glowY }}
        className="absolute -top-40 left-1/4 w-[480px] h-[480px] lg:w-[900px] lg:h-[900px] rounded-full bg-gold/10 blur-[80px] lg:blur-[160px] pointer-events-none"
      />

      <motion.div
        aria-hidden="true"
        style={reduce ? {} : { y: ecoY, opacity: fade }}
        className="absolute inset-0 flex items-end pb-[8vh] justify-center lg:items-center lg:pb-0 lg:justify-end pointer-events-none"
      >
        <motion.div
          initial={reduce ? false : { opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.6, delay: 0.4, ease: EASE }}
          className="w-[540px] sm:w-[620px] lg:w-[760px] lg:mr-[-60px] opacity-30 sm:opacity-40 lg:opacity-55"
        >
          <Ecosystem />
        </motion.div>
      </motion.div>

      <motion.div style={reduce ? {} : { opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-32 lg:pt-36 pb-24 w-full">
        <div className="max-w-3xl">
          <motion.p
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE }}
            className="text-xs uppercase tracking-[0.32em] text-gold font-semibold mb-8"
            data-testid="hero-eyebrow"
          >
            Marketing & Tecnologia para clínicas de saúde e estética
          </motion.p>

          <h1 className="font-display font-extrabold tracking-tight leading-[1.06] text-4xl sm:text-5xl lg:text-6xl" data-testid="hero-headline">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.gold ? "text-gold" : "text-bone"}`}
                  initial={reduce ? false : { y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.25 + i * 0.13, ease: EASE }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={reduce ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
            className="mt-7 text-base sm:text-lg text-bone/65 leading-relaxed max-w-xl"
            data-testid="hero-subheadline"
          >
            Unimos estratégias de captação, estruturação comercial e tecnologia para
            transformar oportunidades em resultados reais.
          </motion.p>

          <motion.div
            initial={reduce ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <button
              data-testid="hero-cta-metodo"
              onClick={() => scrollToSection("#metodo")}
              className="group inline-flex items-center gap-3 bg-gold text-ink font-display font-bold px-7 py-4 rounded-full hover:bg-bone transition-colors duration-300"
            >
              Conheça o Método Pulse
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <a
              data-testid="hero-cta-especialista"
              href={waLink(SPECIALIST_MESSAGE)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 border border-gold/50 text-gold font-display font-bold px-7 py-4 rounded-full hover:bg-gold/10 hover:border-gold transition-all duration-300"
            >
              <MessageCircle size={18} />
              Fale com um especialista
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToSection("#problemas")}
        initial={reduce ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-gold/70 hover:text-gold transition-colors z-10"
        aria-label="Rolar para baixo"
      >
        <ChevronDown size={28} className="animate-float-y" />
      </motion.button>
    </section>
  );
}
